import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RESUME_MAX, emailValide, validateStrict } from '../../utils/creationRequestValidation';
import {
  EXTENSIONS_DOMAINE,
  EXTENSION_PAR_DEFAUT,
  LONGUEUR_MIN_NOM_DOMAINE,
  assemblerDomaine,
  decouperSaisieDomaine,
  estNomDomaineValide
} from '../../utils/domainWish';
import {
  messageErreurDemande,
  useCreateProspectAccount,
  useVerificationEmail
} from '../../hooks/useCreateProspectAccount';
import {
  SERVICES_PAR_METIER,
  descriptionService,
  metierDepuisTexteLibre
} from '../../config/servicesCatalog';

/**
 * Modale « Enregistrer la demande » (étape 2, §2.3 et §7.2).
 *
 * Elle ne crée **rien** elle-même : `createProspectAccount` fait les 5 écritures en un lot côté
 * serveur, et le seul mail part du trigger `onAccountCreated`. Ici on ne fait que : pré-remplir,
 * valider strictement (A7), vérifier l'email (A1/A4), et rendre les 6 états de §2.4 lisibles.
 *
 * Règles d'interface qui ne se négocient pas :
 *  - **la modale ne se ferme jamais sur une erreur** — fermer ferait perdre une saisie de 10 minutes
 *    et l'agent recliquerait, créant un compte orphelin ;
 *  - `telephone` et l'adresse sont **pré-remplis mais éditables** (D1) ; tout le reste est vide
 *    parce que c'est le client qui l'énonce au téléphone ;
 *  - les pièces jointes n'existent pas ici : elles sont optionnelles sur le portail (A8).
 */

/**
 * Pré-remplissage : téléphone + adresse de la fiche (D1), jamais l'identité du contact.
 * Le domaine suit la même logique : s'il a déjà été noté sur la fiche, il revient
 * (nom dans le champ, extension dans le select) ; sinon le champ reste vide.
 */
const initialiserSaisie = (prospect) => {
  const domaineFiche = decouperSaisieDomaine(prospect?.domainWish);
  return {
    email: '',
    firstName: '',
    lastName: '',
    nomCommercial: '',
    telephone: prospect?.phone || '',
    siret: '',
    domainName: domaineFiche.name,
    domainExtension: domaineFiche.extension || EXTENSION_PAR_DEFAUT,
    rue: prospect?.address?.street || '',
    codePostal: prospect?.address?.postalCode || '',
    ville: prospect?.address?.city || '',
    metier: '',
    resumeActivite: '',
    services: []
  };
};

/** Un champ de la modale : libellé, saisie, aide et message d'erreur (chemin pointé). */
const Champ = ({
  label,
  valeur,
  onChange,
  onBlur,
  erreur,
  aide,
  placeholder,
  type = 'text',
  maxLength,
  className = ''
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className="text-sm font-medium text-base-content">{label}</label>
    <input
      type={type}
      className={`input input-bordered input-sm w-full ${erreur ? 'input-error' : ''}`}
      value={valeur}
      onChange={(evenement) => onChange(evenement.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      maxLength={maxLength}
    />
    {erreur ? (
      <p className="text-xs text-error">{erreur}</p>
    ) : aide ? (
      <p className="text-xs text-base-content/50">{aide}</p>
    ) : null}
  </div>
);

/** Une zone numérotée du formulaire (6 zones, §7.2). */
const Zone = ({ numero, titre, children, description }) => (
  <section className="border border-base-300 rounded-lg p-4">
    <div className="flex items-baseline gap-2 mb-3">
      <span className="badge badge-primary badge-sm">{numero}</span>
      <h3 className="font-semibold text-base-content">{titre}</h3>
    </div>
    {description && <p className="text-xs text-base-content/60 mb-3">{description}</p>}
    <div className="space-y-3">{children}</div>
  </section>
);

const CreateProspectRequestModal = ({ prospect, onFermer, onCree }) => {
  const [saisie, setSaisie] = useState(() => initialiserSaisie(prospect));
  const [tentative, setTentative] = useState(false);
  const [touches, setTouches] = useState({});
  const [dejaCree, setDejaCree] = useState(null);

  const verification = useVerificationEmail();
  const { creer, enCours, erreur, reinitialiser } = useCreateProspectAccount();

  const email = saisie.email.trim().toLowerCase();
  const verifierEmail = verification.verifier;

  // A1 : la vérification suit la frappe (debounce interne de 400 ms, §2.3).
  // ⚠️ Dépendance sur `verifierEmail` seule : `verification` est un objet recréé à chaque rendu,
  // le mettre en dépendance relancerait l'effet en boucle.
  useEffect(() => {
    verifierEmail(saisie.email);
  }, [saisie.email, verifierEmail]);

  const formulaire = useMemo(
    () => ({
      personalInfo: { firstName: saisie.firstName, lastName: saisie.lastName },
      businessInfo: {
        nomCommercial: saisie.nomCommercial,
        telephone: saisie.telephone,
        siret: saisie.siret,
        adresse: { rue: saisie.rue, codePostal: saisie.codePostal, ville: saisie.ville }
      },
      activity: { metier: saisie.metier, resumeActivite: saisie.resumeActivite },
      services: saisie.services,
      // Facultatif : `''` quand l'agent n'a rien noté. Toujours une chaîne « monsite.fr »,
      // jamais un objet — c'est le format que relisent `RequestInfo`, `DomainSuggestionsManager`
      // et `LaunchDomainPanel` côté client.
      domainWish: assemblerDomaine({
        name: saisie.domainName,
        extension: saisie.domainExtension
      })
    }),
    [saisie]
  );

  const erreursLocales = useMemo(() => validateStrict(formulaire), [formulaire]);
  // Les erreurs serveur (`details.fields`) écrasent les locales : c'est le serveur qui fait foi.
  const erreurs = useMemo(
    () => ({ ...erreursLocales, ...(erreur?.details?.fields || {}) }),
    [erreursLocales, erreur]
  );

  const emailValidee =
    verification.verifie &&
    verification.email === email &&
    !verification.existe &&
    !verification.enCours &&
    emailValide(email);

  const formulaireValide = Object.keys(erreursLocales).length === 0;
  /** Champ facultatif : seul un nom trop court (1 caractère) empêche la création. */
  const domaineValide = estNomDomaineValide(saisie.domainName);
  const domaineComplet = assemblerDomaine({
    name: saisie.domainName,
    extension: saisie.domainExtension
  });
  const pret = formulaireValide && domaineValide && emailValidee && !enCours;

  /** Message d'erreur d'un champ : masqué tant que l'agent n'a pas quitté le champ. */
  const erreurChamp = (cle) => (tentative || touches[cle] ? erreurs[cle] : undefined);

  const modifier = (cle, valeur) =>
    setSaisie((ancien) => ({ ...ancien, [cle]: valeur }));

  const marquerTouche = (cle) => setTouches((ancien) => ({ ...ancien, [cle]: true }));

  const ajouterService = (nom) =>
    setSaisie((ancien) => ({
      ...ancien,
      services: [
        ...ancien.services,
        {
          nom,
          // Une suggestion du catalogue arrive pré-rédigée avec la ville ; une ligne vierge non.
          description: nom ? descriptionService(nom, ancien.ville) : '',
          prix: 'Sur devis'
        }
      ]
    }));

  const modifierService = (index, champ, valeur) =>
    setSaisie((ancien) => ({
      ...ancien,
      services: ancien.services.map((service, position) =>
        position === index ? { ...service, [champ]: valeur } : service
      )
    }));

  const supprimerService = (index) =>
    setSaisie((ancien) => ({
      ...ancien,
      services: ancien.services.filter((_, position) => position !== index)
    }));

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    setTentative(true);
    reinitialiser();
    if (!pret) return;

    try {
      const resultat = await creer({ placeId: prospect.placeId, email, form: formulaire });
      // Double clic / fiche déjà reliée : aucun doublon, aucun second mail (A5).
      if (resultat?.alreadyCreated) {
        setDejaCree(resultat);
        return;
      }
      toast.success(`Demande créée pour ${email} — un seul mail envoyé.`);
      if (onCree) onCree(resultat);
      if (onFermer) onFermer();
    } catch {
      // Volontairement vide : l'erreur est déjà dans `erreur` (hook) et s'affiche dans le bandeau.
      // La modale reste ouverte, la saisie est intacte (§2.4, état `error`).
    }
  };

  const messageErreur = messageErreurDemande(erreur);
  const metierCanonique = metierDepuisTexteLibre(saisie.metier);
  const suggestions = (SERVICES_PAR_METIER[metierCanonique] || []).filter(
    (suggestion) => !saisie.services.some((service) => service.nom === suggestion.nom)
  );

  /** Pourquoi le bouton est inactif — l'agent ne doit pas deviner (§2.4, état `ready`). */
  const raisonsInactif = [];
  if (!emailValidee) {
    if (!emailValide(email)) raisonsInactif.push('adresse email valide');
    else if (verification.enCours) raisonsInactif.push('vérification de l’adresse en cours');
    else if (verification.existe) raisonsInactif.push('adresse déjà utilisée par un compte');
    else raisonsInactif.push('vérification de l’adresse');
  }
  if (!formulaireValide) raisonsInactif.push('tous les champs obligatoires');
  if (!domaineValide) raisonsInactif.push('un nom de domaine valide (2 caractères minimum)');

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl max-h-[90vh]">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-bold text-base-content">Enregistrer la demande</h2>
            <p className="text-xs text-base-content/60">
              {prospect?.name || 'Fiche prospect'}
              {prospect?.phone ? ` · ${prospect.phone}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={onFermer}
            disabled={enCours}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {dejaCree ? (
          <div className="space-y-4">
            <div className="alert alert-info text-sm">
              <span>
                Cette fiche est déjà reliée à un compte : aucun doublon, aucun second mail n’a été
                créé.
              </span>
            </div>
            <Link to={`/creation-requests?request=${dejaCree.userId}`} className="btn btn-primary btn-sm">
              Voir la demande
            </Link>
            <div className="modal-action">
              <button type="button" className="btn btn-sm" onClick={onFermer}>
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={soumettre} className="space-y-4">
            {messageErreur && (
              <div className="alert alert-error text-sm">
                <span>{messageErreur}</span>
              </div>
            )}

            <Zone numero="1" titre="Contact" description="Ce que le client a énoncé au téléphone.">
              <div className="space-y-1">
                <Champ
                  label="Adresse email"
                  type="email"
                  valeur={saisie.email}
                  onChange={(valeur) => modifier('email', valeur)}
                  onBlur={() => marquerTouche('email')}
                  placeholder="client@exemple.fr"
                  aide={
                    verification.enCours
                      ? 'Vérification de l’adresse…'
                      : verification.verifie && !verification.existe && email === verification.email
                        ? 'Adresse disponible ✓'
                        : 'Sert d’identifiant de connexion — un seul mail sera envoyé.'
                  }
                  erreur={
                    verification.erreur
                      ? 'Vérification impossible (réseau) — ressaisissez l’adresse.'
                      : verification.existe
                        ? 'Un compte MonSiteDirect existe déjà avec cette adresse.'
                        : saisie.email && !emailValide(saisie.email)
                          ? 'Adresse email invalide.'
                          : undefined
                  }
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Champ
                  label="Prénom"
                  valeur={saisie.firstName}
                  onChange={(valeur) => modifier('firstName', valeur)}
                  onBlur={() => marquerTouche('personalInfo.firstName')}
                  erreur={erreurChamp('personalInfo.firstName')}
                  placeholder="Jean"
                />
                <Champ
                  label="Nom"
                  valeur={saisie.lastName}
                  onChange={(valeur) => modifier('lastName', valeur)}
                  onBlur={() => marquerTouche('personalInfo.lastName')}
                  erreur={erreurChamp('personalInfo.lastName')}
                  placeholder="Dupont"
                />
              </div>
            </Zone>

            <Zone
              numero="2"
              titre="Entreprise"
              description="Téléphone et adresse pré-remplis depuis la fiche, modifiables. Domaine facultatif."
            >
              <Champ
                label="Nom commercial"
                valeur={saisie.nomCommercial}
                onChange={(valeur) => modifier('nomCommercial', valeur)}
                onBlur={() => marquerTouche('businessInfo.nomCommercial')}
                erreur={erreurChamp('businessInfo.nomCommercial')}
                placeholder="Dupont Plomberie"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Champ
                  label="Téléphone"
                  valeur={saisie.telephone}
                  onChange={(valeur) => modifier('telephone', valeur)}
                  onBlur={() => marquerTouche('businessInfo.telephone')}
                  erreur={erreurChamp('businessInfo.telephone')}
                  placeholder="0612345678"
                  aide="Format 06… ou +33…"
                />
                <Champ
                  label="SIRET (facultatif)"
                  valeur={saisie.siret}
                  onChange={(valeur) => modifier('siret', valeur)}
                  onBlur={() => marquerTouche('businessInfo.siret')}
                  erreur={erreurChamp('businessInfo.siret')}
                  placeholder="14 chiffres"
                  maxLength={14}
                />
              </div>

              {/* Domaine souhaité : nom saisi + extension choisie (anti « monsite.fr.fr »). */}
              <div className="space-y-1">
                <label
                  className="text-sm font-medium text-base-content"
                  htmlFor="domaine-souhaite"
                >
                  Domaine souhaité{' '}
                  <span className="text-base-content/50 font-normal">(facultatif)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="domaine-souhaite"
                    type="text"
                    className={`input input-bordered input-sm flex-1 ${
                      domaineValide ? '' : 'input-error'
                    }`}
                    value={saisie.domainName}
                    onChange={(evenement) => {
                      const saisieDecoupee = decouperSaisieDomaine(evenement.target.value);
                      setSaisie((ancien) => ({
                        ...ancien,
                        domainName: saisieDecoupee.name,
                        // Une extension tapée (« monsite.com ») prend la main sur le select.
                        domainExtension: saisieDecoupee.extension || ancien.domainExtension
                      }));
                    }}
                    placeholder="dupont-plomberie"
                    maxLength={63}
                  />
                  <select
                    className="select select-bordered select-sm w-28"
                    value={saisie.domainExtension}
                    onChange={(evenement) => modifier('domainExtension', evenement.target.value)}
                    aria-label="Extension du domaine souhaité"
                  >
                    {EXTENSIONS_DOMAINE.map((extension) => (
                      <option key={extension} value={extension}>{`.${extension}`}</option>
                    ))}
                  </select>
                </div>
                {domaineValide ? (
                  <p className="text-xs text-base-content/50">
                    {domaineComplet
                      ? `Sera enregistré : ${domaineComplet} · disponibilité vérifiée plus tard.`
                      : 'Uniquement si le client a donné un nom au téléphone — sans l’extension.'}
                  </p>
                ) : (
                  <p className="text-xs text-error">
                    {`Nom trop court : ${LONGUEUR_MIN_NOM_DOMAINE} caractères minimum avant l’extension.`}
                  </p>
                )}
              </div>
            </Zone>

            <Zone numero="3" titre="Adresse" description="Adresse pré-remplie depuis la fiche, éditable.">
              <Champ
                label="Rue"
                valeur={saisie.rue}
                onChange={(valeur) => modifier('rue', valeur)}
                onBlur={() => marquerTouche('businessInfo.adresse.rue')}
                erreur={erreurChamp('businessInfo.adresse.rue')}
                placeholder="12 Rue des Lilas"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Champ
                  label="Code postal"
                  valeur={saisie.codePostal}
                  onChange={(valeur) => modifier('codePostal', valeur)}
                  onBlur={() => marquerTouche('businessInfo.adresse.codePostal')}
                  erreur={erreurChamp('businessInfo.adresse.codePostal')}
                  placeholder="60000"
                  maxLength={5}
                />
                <Champ
                  className="sm:col-span-2"
                  label="Ville"
                  valeur={saisie.ville}
                  onChange={(valeur) => modifier('ville', valeur)}
                  onBlur={() => marquerTouche('businessInfo.adresse.ville')}
                  erreur={erreurChamp('businessInfo.adresse.ville')}
                  placeholder="Beauvais"
                />
              </div>
            </Zone>

            <Zone numero="4" titre="Activité" description="Le métier pilote les prestations proposées.">
              <Champ
                label="Métier / activité"
                valeur={saisie.metier}
                onChange={(valeur) => modifier('metier', valeur)}
                onBlur={() => marquerTouche('activity.metier')}
                erreur={erreurChamp('activity.metier')}
                placeholder="Plomberie"
                aide="Écrit tel que le client l’a dit — propose les prestations du catalogue."
              />
              <div className="space-y-1">
                <label className="text-sm font-medium text-base-content">Résumé de l’activité</label>
                <textarea
                  className={`textarea textarea-bordered w-full h-24 ${
                    erreurChamp('activity.resumeActivite') ? 'textarea-error' : ''
                  }`}
                  value={saisie.resumeActivite}
                  onChange={(evenement) => modifier('resumeActivite', evenement.target.value)}
                  onBlur={() => marquerTouche('activity.resumeActivite')}
                  maxLength={RESUME_MAX}
                  placeholder="Plombier chauffagiste depuis 1985, dépannage et entretien de chaudière."
                />
                <div className="flex justify-between gap-3 text-xs">
                  <span className={erreurChamp('activity.resumeActivite') ? 'text-error' : 'text-base-content/50'}>
                    {erreurChamp('activity.resumeActivite') || '50 caractères minimum, en 2 phrases.'}
                  </span>
                  <span className="text-base-content/50 shrink-0">
                    {saisie.resumeActivite.trim().length}/{RESUME_MAX}
                  </span>
                </div>
              </div>
            </Zone>

            <Zone
              numero="5"
              titre="Prestations"
              description="Cochées au catalogue de l’étape 1, éditables — une ligne = un service du site."
            >
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.nom}
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={() => ajouterService(suggestion.nom)}
                    >
                      + {suggestion.nom}
                    </button>
                  ))}
                </div>
              )}

              {saisie.services.length === 0 && (
                <p className="text-xs text-base-content/60">
                  Aucune prestation : ajoutez-en au moins une, sinon la demande ne peut pas être
                  enregistrée.
                </p>
              )}

              {erreurChamp('services') && <p className="text-xs text-error">{erreurs.services}</p>}

              {saisie.services.map((service, index) => (
                <div key={index} className="border border-base-300 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-base-content/60">
                      Prestation {index + 1}
                    </span>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost text-error"
                      onClick={() => supprimerService(index)}
                    >
                      Supprimer
                    </button>
                  </div>
                  <Champ
                    label="Nom"
                    valeur={service.nom}
                    onChange={(valeur) => modifierService(index, 'nom', valeur)}
                    erreur={erreurChamp(`services.${index}.nom`)}
                  />
                  <Champ
                    label="Description"
                    valeur={service.description}
                    onChange={(valeur) => modifierService(index, 'description', valeur)}
                    erreur={erreurChamp(`services.${index}.description`)}
                  />
                  <Champ
                    label="Prix"
                    valeur={service.prix}
                    onChange={(valeur) => modifierService(index, 'prix', valeur)}
                    erreur={erreurChamp(`services.${index}.prix`)}
                    placeholder="Sur devis"
                  />
                </div>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => ajouterService('')}
              >
                + Ajouter une prestation
              </button>
            </Zone>

            <Zone
              numero="6"
              titre="Récapitulatif"
              description="Dernier contrôle avant l’envoi — rien n’est encore écrit."
            >
              <ul className="text-sm text-base-content/80 space-y-1">
                <li>
                  <span className="text-base-content/50">Contact : </span>
                  {`${saisie.firstName} ${saisie.lastName}`.trim() || '—'} · {email || '—'}
                </li>
                <li>
                  <span className="text-base-content/50">Entreprise : </span>
                  {saisie.nomCommercial || '—'} · {saisie.telephone || '—'}
                  {saisie.siret ? ` · SIRET ${saisie.siret}` : ' · SIRET non fourni'}
                </li>
                <li>
                  <span className="text-base-content/50">Adresse : </span>
                  {`${saisie.rue}, ${saisie.codePostal} ${saisie.ville}`.trim() || '—'}
                </li>
                <li>
                  <span className="text-base-content/50">Activité : </span>
                  {saisie.metier || '—'} · {saisie.services.length} prestation
                  {saisie.services.length > 1 ? 's' : ''}
                </li>
                <li>
                  <span className="text-base-content/50">Domaine souhaité : </span>
                  {domaineComplet || 'non précisé (propositions de l’équipe)'}
                </li>
              </ul>
              <div className="alert alert-warning text-xs">
                <span>
                  Un seul email sera envoyé (bienvenue + lien d’activation valable 30 jours). Pas de
                  relance automatique : si le client ne clique pas, l’agent relance par téléphone.
                </span>
              </div>
            </Zone>

            {raisonsInactif.length > 0 && (
              <p className="text-xs text-base-content/60">
                Encore requis : {raisonsInactif.join(', ')}.
              </p>
            )}

            <div className="modal-action sticky bottom-0 bg-base-100 pt-2">
              <button type="button" className="btn btn-sm" onClick={onFermer} disabled={enCours}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!pret}>
                {enCours ? (
                  <span className="flex items-center gap-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    Création…
                  </span>
                ) : (
                  'Enregistrer la demande'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateProspectRequestModal;
